// src/api/axios.ts
import axios, { InternalAxiosRequestConfig, AxiosHeaders } from "axios";

// 🛡️ SECURITY: In production, use relative /api path so requests route through CloudFront.
// CloudFront injects the x-custom-secret-header automatically.
// Direct API Gateway calls are blocked by the backend's origin shield.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  headers: { "Content-Type": "application/json" },
});


// Add token to all requests
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // 🛡️ SMART TOKEN SELECTION: Use platform_user for platform routes, else user
  const isPlatformRoute = window.location.pathname.includes("/platform");
  const platformKey = "platform_user";
  const userKey = "user";
  
  const saved = localStorage.getItem(isPlatformRoute ? platformKey : userKey);
  
  if (saved && saved !== "undefined") {
    try {
      const parsed = JSON.parse(saved);
      if (parsed.token) {
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }
        config.headers.set("Authorization", `Bearer ${parsed.token}`);
      }
    } catch (e) {
      console.error("Failed to parse auth token from localStorage", e);
    }
  }
  return config;
});

// Response logger & 401 Handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 🛡️ SECURITY: A 401 on a protected endpoint means the session is gone —
    // whether the token was present-but-stale/revoked, or missing entirely
    // (e.g. another tab's idle-logout wiped localStorage while this tab was
    // still open). Either way the right move is to redirect to login, so we
    // do NOT gate this on whether an Authorization header happened to be
    // attached — a missing header on a protected route used to silently skip
    // the redirect, leaving the user stuck on a broken page until they
    // manually logged out.
    // Pre-auth endpoints (login, register, OTP, forgot-password — all under
    // /auth/*) legitimately return 401 for business reasons (bad credentials,
    // deactivated account) and must NOT be redirected, or we'd wipe out the
    // error the caller is about to render before the user ever sees it.
    // Some AUTHENTICATED endpoints also return 401 for a legitimate business
    // reason (e.g. wrong delete-account OTP) rather than session revocation —
    // those opt out explicitly via { skipSessionRedirect: true }.
    const requestUrl: string = error.config?.url || "";
    const isPublicAuthEndpoint = requestUrl.startsWith("/auth/") || requestUrl.includes("/auth/");
    const skipRedirect = !!(error.config as any)?.skipSessionRedirect || isPublicAuthEndpoint;
    if (error.response?.status === 401 && !skipRedirect) {
      console.warn("🔐 Session expired. Redirecting to login...");

      // Clear potentially stale storage
      localStorage.removeItem("user");
      localStorage.removeItem("platform_user");

      // Force redirect to the correct login page based on current URL
      if (window.location.pathname.startsWith("/platform")) {
        window.location.href = "/platform/login?expired=true";
      } else {
        window.location.href = "/login?expired=true";
      }
    }

    console.error(
      "❌ Response Error:",
      error.response?.status,
      error.response?.data
    );
    return Promise.reject(error);
  }
);

export default api;
